import { Injectable } from '@angular/core';
import { ActivatedRoute, Router, UrlTree } from '@angular/router';
import { ActiveCartFacade } from '@spartacus/cart/base/root';
import {
  AuthRedirectService,
  AuthService,
  B2BUser,
  B2BUserRole,
  GlobalMessageService,
  GlobalMessageType,
  SemanticPathService,
} from '@spartacus/core';
import { User, UserAccountFacade } from '@spartacus/user/account/root';
import { Observable, combineLatest, of } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { CheckoutConfigService } from '../services/checkout-config.service';
import { CartActions, StateWithMultiCart } from '@spartacus/cart/base/core';
import { Store } from '@ngrx/store';

@Injectable({
  providedIn: 'root',
})
export class CheckoutAuthGuard {
  constructor(
    protected authService: AuthService,
    protected authRedirectService: AuthRedirectService,
    protected checkoutConfigService: CheckoutConfigService,
    protected activeCartService: ActiveCartFacade,
    protected semanticPathService: SemanticPathService,
    protected router: Router,
    protected userService: UserAccountFacade,
    protected globalMessageService: GlobalMessageService,
    protected activatedRoute: ActivatedRoute,
    protected store: Store<StateWithMultiCart>
  ) {}

  canActivate(): Observable<boolean | UrlTree> {
    return combineLatest([
      this.authService.isUserLoggedIn(),
      this.activeCartService.isGuestCart(),
      this.activeCartService.isStable(),
    ]).pipe(
      map(
        ([
          isLoggedIn,
          isGuestCart,
          isStable,
        ]) => ({
          isLoggedIn,
          isGuestCart,
          isStable,
        })
      ),
      filter((data) => data.isStable),
      map((data) => {
        if (!data.isLoggedIn) {
          if (this.router.getCurrentNavigation()?.extractedUrl.queryParams?.guestCart) {
            this.store.dispatch(
              new CartActions.SetActiveCartId(this.router.getCurrentNavigation()?.extractedUrl.queryParams?.guestCart)
            );
            return true;
          }

          return data.isGuestCart ? true : this.handleAnonymousUser();
        }
        return data.isLoggedIn;
      })
    );
  }

  protected handleAnonymousUser(): boolean | UrlTree {
    this.authRedirectService.saveCurrentNavigationUrl();
    if (this.checkoutConfigService.isGuestCheckout()) {
      return this.router.createUrlTree([this.semanticPathService.get('login')], { queryParams: { forced: true } });
    } else {
      return this.router.parseUrl(this.semanticPathService.get('login') ?? '');
    }
  }

  protected handleUserRole(user: User): boolean | UrlTree {
    const roles = (<B2BUser>user).roles;
    if (roles?.includes(B2BUserRole.CUSTOMER)) {
      return true;
    }
    this.globalMessageService.add({ key: 'checkout.invalid.accountType' }, GlobalMessageType.MSG_TYPE_WARNING);
    return this.router.parseUrl(this.semanticPathService.get('home') ?? '');
  }
}
